/**
 * The hidden archetypes (the owner, Oct 4: per-mode hidden bot configs; decision 121's third stage, over the catalogue
 * — src/ai/botParams.ts). A small set of named configs, each a handful of catalogue values on top of today's bot; with
 * `MatchSetup.archetypes` on, every team draws one by the seed and the mode (or is given one), the player is never told,
 * and the harness reports which team played which. Off by default: today's bots exactly.
 */
import type { BotParamSet, BotParamsConfig } from '../types';
import { keyedGenerator } from '../core/random';

export type ArchetypeName = 'cautious' | 'aggressive' | 'sneaky' | 'artillery';
export const ARCHETYPE_NAMES: readonly ArchetypeName[] = ['cautious', 'aggressive', 'sneaky', 'artillery'];

/** Each archetype: what it is for, and its catalogue values (validated against the catalogue by the tests). */
export const ARCHETYPES: Record<ArchetypeName, { note: string; params: BotParamSet }> = {
  cautious: {
    note: 'keeps to cover, pulls back early, fights from further off',
    params: {
      'turn.tuning.movement.coverWeight': 0.6, 'turn.tuning.postFire.coverHoldBonus': 0.4, 'turn.tuning.action.fireAggression': 0.15,
      'turn.tuning.jumping.maxLandingHealthFraction': 0.5,
      'action.jobs.retreat': 2.2, 'action.jobs.engage': 0.8, 'action.rules.coverHealth': 70,
      'action.positions.cover': 3, 'action.positions.exposure': 2.5, 'action.positions.rangeMin': 9, 'action.positions.rangeMax': 20,
    },
  },
  aggressive: {
    note: 'closes in, pushes points under fire, pulls back late',
    params: {
      'turn.tuning.action.fireAggression': 0.6, 'turn.tuning.movement.closeInAbove': 0.2, 'turn.tuning.action.moveBase': 0.2,
      'action.jobs.engage': 1.4, 'action.jobs.retreat': 1.0, 'action.jobs.takeUnderFire': 0.9, 'action.rules.coverHealth': 30,
      'action.positions.rangeMin': 4, 'action.positions.rangeMax': 11,
    },
  },
  sneaky: {
    note: 'stays unseen, searches, goes prone at range',
    params: {
      'turn.tuning.stealth.sneak': true, 'turn.tuning.stealth.goLoudStalledTurns': 4,
      'action.positions.exposure': 3, 'action.positions.los': 2, 'action.jobs.search': 0.8, 'action.prone.longRange': 1.2,
    },
  },
  artillery: {
    note: 'stands off, holds ground, runs for the guns',
    params: {
      'turn.tuning.weapon.blastNorm': 10, 'turn.tuning.movement.closeInAbove': 0.9,
      'action.gun.runLengths': 16, 'action.jobs.hold': 1.4, 'action.jobs.engage': 0.8,
      'action.positions.rangeMin': 10, 'action.positions.rangeMax': 24, 'action.positions.height': 1,
    },
  },
};

/** The archetype team `team` draws for this seed and mode (a keyed draw: the same seed and mode, the same archetypes). */
export function archetypeFor(seed: string, mode: string, team: number): ArchetypeName {
  const r = keyedGenerator(seed, team, 0, `archetype:${mode}`);
  return ARCHETYPE_NAMES[Math.min(ARCHETYPE_NAMES.length - 1, Math.floor(r.next() * ARCHETYPE_NAMES.length))];
}

/** Each team's archetype under a setup's switch (`true` = drawn; a list = given, a missing entry drawn), or null when off. */
export function archetypesFor(setup: { seed: string; mode?: string; teams?: number; archetypes?: boolean | ReadonlyArray<ArchetypeName | undefined> }): ArchetypeName[] | null {
  if (!setup.archetypes) return null;
  const teams = setup.teams ?? 2;
  const given = Array.isArray(setup.archetypes) ? setup.archetypes : [];
  return Array.from({ length: teams }, (_, t) => given[t] ?? archetypeFor(setup.seed, setup.mode ?? 'default', t));
}

/**
 * The bot params a match plays with: its teams' archetypes' values (when it has them), with the setup's own file on top —
 * a value the file sets wins (everyone's, then the team's). Setup draws the archetypes once and keeps them in the state
 * (`state.botArchetypes`); every host builds the params from there, so none can draw differently.
 */
export function withArchetypes(config: BotParamsConfig | undefined, names: readonly string[] | undefined): BotParamsConfig | undefined {
  if (!names?.length) return config;
  return { byTeam: names.map((n, t) => ({ ...(ARCHETYPES[n as ArchetypeName]?.params ?? {}), ...(config?.params ?? {}), ...(config?.byTeam?.[t] ?? {}) })) };
}
